import { Box, TextField, Typography } from '@mui/material';
import { useContext, useMemo } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { AddressProps } from '@/models/Address';
import { BillingContact } from '@/models/Contact';
import { getCountryByCountryId, getCountryIdByCountryName } from '@/utils/addressUtil';
import { USCountryId } from '@/utils/constants';

import Address from '../common/address_components/Address';

function BillingContactForm({
  contact,
  onChange,
  submitted,
}: {
  contact: BillingContact;
  onChange: (updatedContact: BillingContact) => void;
  submitted: boolean;
}) {
  const { translatedText } = useContext(LocaleContext);
  const { allCountries } = useContext(ConfigContext);

  const handleContactTextFieldChanged: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const updatedContact = {
      ...contact,
      [event.target.name]: event.target.value,
    };
    onChange(updatedContact);
  };

  const handleAddressChanged = (address: AddressProps) => {
    const updatedContact = {
      ...contact,
      address: address.streetAddress1 ?? '',
      address2: address.streetAddress2 ?? '',
      city: address.city ?? '',
      region: address.stateId ?? '',
      postalCode: address.zipCode ?? '',
      country: address.countryId
        ? (getCountryByCountryId(allCountries, address.countryId)?.name ?? '')
        : '',
    };

    onChange(updatedContact);
  };

  const billingAddress: AddressProps = useMemo(() => {
    return {
      streetAddress1: contact.address,
      streetAddress2: contact.address2,
      city: contact.city,
      stateId: contact.region,
      zipCode: contact.postalCode,
      countryId: getCountryIdByCountryName(allCountries, contact.country) ?? USCountryId,
    };
  }, [allCountries, contact]);

  return (
    <Box>
      <Typography variant='h5'>Billing Address</Typography>

      <Box mt={2} mb={2}>
        <TextField
          id={'billing-name'}
          label={translatedText.contact.labelName}
          required
          name={'name'}
          type='text'
          error={submitted && (contact.name == '' || contact.name == null)}
          fullWidth
          variant='outlined'
          value={contact.name}
          onChange={handleContactTextFieldChanged}
        />
      </Box>

      <Address address={billingAddress} onChange={handleAddressChanged} hideCounty />
    </Box>
  );
}

export default BillingContactForm;
